import ImageFallback from 'react-image-fallback';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Data } from '../../../../../service/global';
import { MDBCard, MDBCardBody, MDBIcon } from 'mdb-react-ui-kit';
import { StatusIcon } from '../../../../../components/StatusIcon';
import { MachinePowerButton } from '../../../../../components/MachinePowerButton';
import { MachineConnectionButton } from '../../../../../components/MachineConnectionButton';
import { NavLink } from 'react-router-dom';
import { FixedSizeList, FixedSizeList as List } from 'react-window';

import { useWindowSize } from '../../../../../hooks/useWindowSize';
import { SearchInput } from '../../../../../components/SearchInput';
import { OpMachineListItemDTO } from '@scm/common-legacy';

export const MachinesList = ({
    machines,
}: {
    machines: OpMachineListItemDTO[];
}) => {
    const { t } = useTranslation();
    const { innerWidth } = useWindowSize();
    const [containerHeight, setContainerHeight] = useState(0);
    const [search, setSearch] = useState('');
    const twoColumnDisplay = useMemo(() => innerWidth >= 1200, [innerWidth]);

    const sortedMachinesList = useMemo(() => {
        return !machines
            ? []
            : machines
                  .filter((machine) =>
                      machine.name.toLowerCase().includes(search.toLowerCase()),
                  )
                  .map(
                      (machine) =>
                          ({
                              machine,
                              priority:
                                  machine.status
                                      .map(
                                          (s) =>
                                              Data.esList[s.id]?.priority ?? 0,
                                      )
                                      .sort((a, b) => b - a)[0] ??
                                  (!machine.power || !machine.connection
                                      ? 1
                                      : -1),
                          }) as {
                              machine: OpMachineListItemDTO;
                              priority: number;
                          },
                  )
                  .sort((a, b) => b.priority - a.priority)
                  .map(({ machine }) => machine);
    }, [machines, search]);

    const logContainerRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<FixedSizeList>(null);

    useEffect(() => {
        if (!logContainerRef.current) return;
        const observer = new ResizeObserver(() => {
            setContainerHeight(
                logContainerRef.current?.getBoundingClientRect().height || 0,
            );
        });
        observer.observe(logContainerRef.current);
        return () => observer.disconnect();
    }, [logContainerRef]);

    return (
        <div className="d-flex flex-column w-100 flex-fill overflow-hidden">
            <div
                className={`px-2 mb-2 d-flex flex-row justify-content-between ${twoColumnDisplay ? 'w-50' : 'w-100'}`}
            >
                <SearchInput
                    onSearch={setSearch}
                    className=""
                    style={{ maxWidth: '100%' }}
                />
            </div>

            <div
                ref={logContainerRef}
                className="d-flex flex-fill overflow-auto w-100 px-2"
            >
                {(!machines || machines.length == 0) && (
                    <span className="d-block text-muted fst-italic w-100 text-center">
                        {t('no_machines')}
                    </span>
                )}
                {machines?.length > 0 && (
                    <List
                        ref={listRef}
                        height={containerHeight}
                        width="100%"
                        itemSize={80}
                        itemCount={
                            twoColumnDisplay
                                ? Math.ceil(sortedMachinesList.length / 2)
                                : sortedMachinesList.length
                        }
                    >
                        {({ index, style }) => (
                            <>
                                {!twoColumnDisplay && (
                                    <div
                                        style={style}
                                        className="overflow-hidden py-1 px-1 w-100"
                                    >
                                        <MachineItem
                                            machine={sortedMachinesList[index]}
                                        />
                                    </div>
                                )}
                                {twoColumnDisplay && (
                                    <div
                                        className="d-flex flex-row w-100 overflow-hidden"
                                        style={style}
                                    >
                                        <div className="d-flex w-50 px-1">
                                            <MachineItem
                                                machine={
                                                    sortedMachinesList[
                                                        2 * index
                                                    ]
                                                }
                                            />
                                        </div>

                                        {2 * index + 1 <
                                            sortedMachinesList.length && (
                                            <div className="d-flex w-50 px-1">
                                                <MachineItem
                                                    machine={
                                                        sortedMachinesList[
                                                            2 * index + 1
                                                        ]
                                                    }
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </List>
                )}
            </div>
        </div>
    );
};

const MachineItem = ({ machine }: { machine: OpMachineListItemDTO }) => {
    const { t } = useTranslation();
    const bg = useMemo<string | null>(
        () =>
            (Data.esList[machine.status[0]?.id]?.web?.level as string) ||
            (!machine.power || !machine.connection ? 'gray' : null),
        [machine],
    );
    const expired = useMemo(
        () =>
            machine.expireAt &&
            new Date(machine.expireAt).getTime() <= new Date().getTime(),
        [machine.expireAt],
    );

    return (
        <div className={`d-inline-block w-100${expired ? ' opacity-50' : ''}`}>
            <MDBCard
                className={`card-hover2 shadow-2-strong p-1 bg-opacity-25 bg-${bg} machine-item`}
            >
                <NavLink to={`machines/${machine.id}`}>
                    <MDBCardBody className="p-1 w-100 h-100">
                        <div
                            className="d-flex flex-column"
                            style={{
                                alignItems: 'baseline',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            <div className="d-flex flex-row align-items-center w-100 mb-1">
                                <div className="d-flex flex-row pe-2 flex-fill overflow-hidden">
                                    <span
                                        className="me-1 d-flex align-items-center"
                                        style={{ height: 22 }}
                                        // data-tooltip-id="mtype-tooltip" data-tooltip-content={machine.type?.name||'?'}
                                        onClick={(ev) => {
                                            ev.preventDefault();
                                            ev.stopPropagation();
                                            return false;
                                        }}
                                    >
                                        <img
                                            src={`/mtypes/mt${machine.type?.id ?? 0}.png`}
                                            width={22}
                                            height={22}
                                        />
                                        {/* <ImageFallback src={`/mtypes/mt${machine.type?.id??0}.png`} width={22} height={22} fallbackImage={<span></span>}/> */}
                                    </span>
                                    {/* <Tooltip id={`mtype-tooltip`} place="right"/> */}
                                    <span className="text-dark fw-bold overflow-hidden me-1">
                                        {machine.name}
                                    </span>
                                </div>
                                {machine.locked && (
                                    <span title={t('machine:machine_locked')}>
                                        <MDBIcon
                                            icon="lock"
                                            color="danger"
                                            size="lg"
                                            className="me-1"
                                        />
                                    </span>
                                )}
                                <MachineConnectionButton
                                    machine={machine}
                                    tooltipId={`${machine.id}-connection-status`}
                                />
                                <MachinePowerButton
                                    machine={machine}
                                    tooltipId={`${machine.id}-power-status`}
                                />
                            </div>

                            <div className="d-flex flex-row align-items-center flex-fill w-100">
                                {machine.status.map((s) => (
                                    <StatusIcon
                                        key={s.id}
                                        name={s.id}
                                        count={s.count}
                                        ts={s.ts}
                                        badge={machine.status.length == 1}
                                    />
                                ))}

                                <span className="d-flex flex-row align-items-center w-100 flex-fill justify-content-end">
                                    {typeof machine.value == 'number' && (
                                        <>
                                            <span className="badge badge-primary text-dark">
                                                <MDBIcon
                                                    icon="hand-holding-dollar"
                                                    color="primary"
                                                    className="me-1"
                                                />
                                                <span>{machine.value}</span>
                                            </span>
                                        </>
                                    )}
                                </span>
                            </div>
                        </div>
                    </MDBCardBody>
                </NavLink>
            </MDBCard>
        </div>
    );
};
